import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, Typography, styled, Skeleton, CircularProgress, Fade } from "@mui/material";
import { PrimarySpinner } from "../../../../spinner/primary-spinner";
import { useApolloClient, useLazyQuery, useSubscription } from "@apollo/client";
import { useInView } from "react-intersection-observer";
import {
  GET_MESSAGES,
  GET_USER_CHATS,
} from "../../../../../apollo-client/queries";
import { MessageWithTime } from "./messageWithTime";
import {
  MESSAGE_SUBSCRIPTION,
  NEW_MESSAGE_STATE_SUBSCRIPTION,
} from "../../../../../apollo-client/subscriptions";
import { FormattedMessage } from "react-intl";
import { QuestionMarkTooltip } from "../../../../common/tooltip/question-mark-tooltip";
import { isEmpty } from "lodash";
import { GET_TOTAL_UNREAD_MESSAGES_COUNT } from "../../../courseNav/small-course-nav/getTotalUnreadMessagesCountQuery";

const messagesLimit = 30;

export const MessageContainer = ({ chosenChat, postingMessage }) => {
  const scrollRef = useRef(null);
  const endOfMessagesRef = useRef(null);

  const client = useApolloClient();

  const [fixedDate, setFixedDate] = useState(null);
  const [toggleFixedDate, setToggleFixedDate] = useState(true);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  // Intersection Observer for loading more messages
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '100px 0px',
    triggerOnce: false,
  });

  const scrollToBottom = useCallback((behavior = "auto") => {
    endOfMessagesRef.current?.scrollIntoView({ behavior });
  }, []);

  const [
    getMessages,
    {
      data: dataMessages,
      fetchMore: fetchMoreMessages,
      loading: loadingMessages,
    },
  ] = useLazyQuery(GET_MESSAGES, {
    variables: {
      _id: chosenChat.chatId,
      limit: messagesLimit,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const messages = data?.user?.chats?.chats?.[0]?.messages || [];
      setHasMoreMessages(messages.length === messagesLimit);
      scrollToBottom("smooth");
    },
  });

  const chatMessagesData = dataMessages?.user?.chats?.chats?.[0];

  // Load more messages when intersection observer triggers
  const loadMoreMessages = useCallback(async () => {
    if (loadingOlderMessages || !hasMoreMessages || !chatMessagesData) return;
    
    setLoadingOlderMessages(true);
    try {
      const result = await fetchMoreMessages({
        variables: {
          limit: messagesLimit,
          skip: chatMessagesData.messages.length,
        },
      });
      
      const newMessages = result.data?.user?.chats?.chats?.[0]?.messages || [];
      setHasMoreMessages(newMessages.length === messagesLimit);
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [loadingOlderMessages, hasMoreMessages, chatMessagesData, fetchMoreMessages]);

  // Trigger load more when intersection observer detects the trigger element
  useEffect(() => {
    if (inView && hasMoreMessages && !loadingOlderMessages) {
      loadMoreMessages();
    }
  }, [inView, hasMoreMessages, loadingOlderMessages, loadMoreMessages]);

  // Load messages when selecting a new chat
  useEffect(() => {
    const loadMessages = async () => {
      setHasMoreMessages(true); // Reset pagination state
      await getMessages();
      scrollToBottom("smooth");
    };
    loadMessages();
  }, [chosenChat, getMessages]);

  // Show the posting message until new messages are loaded
  useEffect(() => {
    if (postingMessage) {
      scrollToBottom("smooth");
    }
  }, [postingMessage, scrollToBottom]);

  useSubscription(MESSAGE_SUBSCRIPTION, {
    onSubscriptionData: async () => {
      await client.refetchQueries({
        include: [GET_USER_CHATS, GET_TOTAL_UNREAD_MESSAGES_COUNT],
      });
      scrollToBottom("smooth");
    },
  });

  useSubscription(NEW_MESSAGE_STATE_SUBSCRIPTION);

  // Render skeleton loading placeholders
  const renderSkeletonMessages = () => (
    <>
      {/* Received message skeleton */}
      <SkeletonMessage isMyMessage={false} />
      {/* User message skeleton */}
      <SkeletonMessage isMyMessage={true} />
      {/* Another received message skeleton */}
      <SkeletonMessage isMyMessage={false} />
    </>
  );

  return (
    <Container
      ref={scrollRef}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        margin: "35px 0 0px 0", // Reduced bottom margin to eliminate gap
        overflowY: "auto",
        flex: 1, // Take up available space
        minHeight: 0, // Allow container to shrink
        "&::-webkit-scrollbar-track": {
          borderRadius: "10px",
          backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar": {
          width: "6px",
          backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: "10px",
          backgroundColor: "transparent",
        },
        "&:hover": {
          "&::-webkit-scrollbar-track": {
            WebkitBoxShadow: "transparent",
            borderRadius: "0px",
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: "0px",
            WebkitBoxShadow: "transparent",
            backgroundColor: (theme) => theme.scrollBarColor || "lightgrey",
          },
        },
      }}
    >
      {/* Load more trigger at top - only show when there are more messages */}
      {hasMoreMessages && (
        <Box 
          ref={loadMoreRef} 
          sx={{ 
            minHeight: '20px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            py: 1
          }}
        >
          <Fade in={loadingOlderMessages}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="caption" color="text.secondary">
                Loading older messages...
              </Typography>
            </Box>
          </Fade>
        </Box>
      )}

      {loadingMessages && !chatMessagesData && (
        <ModernLoadingIndicator />
      )}
      
      {loadingMessages && !chatMessagesData && renderSkeletonMessages()}
      
      {chatMessagesData?.messages?.map((messageObject, messageIndex) => {
        const messages = chatMessagesData.messages;
        return (
          <MessageWithTime
            key={messageObject._id}
            messageObject={messageObject}
            messageIndex={messageIndex}
            messages={messages}
            chatType={chosenChat.type}
            showDateSeparator={
              messageIndex === 0 ||
              !messageObject?.wasReadByMe &&
              messages[messageIndex - 1]?.wasReadByMe
            }
          />
        );
      })}
      {/* Render the posting message optimistically */}
      {postingMessage && (
        <MessageWithTime
          messageObject={{
            _id: `temp-${Date.now()}`,
            author: { username: "You" },
            isAuthor: true,
            message: postingMessage,
            createdAt: Date.now(),
            wasDelivered: false,
            wasRead: false,
          }}
          messageIndex={-1} // Temporary index
          messages={[]} // Empty array for context
          chatType={chosenChat.type}
          postingMessage={true} // Indicate it's a posting message
        />
      )}
      <div ref={endOfMessagesRef} />
      
      {!isEmpty(chosenChat?.isBlockedBy) && (
        <Row>
          <IsBlockedText>
            <FormattedMessage id={"chat.isBlocked"} />
          </IsBlockedText>
          <QuestionMarkTooltip tooltipId={"chat.isBlocked.tooltip"} />
        </Row>
      )}
    </Container>
  );
};

const Container = styled(Box)({});

const ModernLoadingIndicator = () => (
  <ModernLoadingIndicatorWrapper>
    <CircularProgress size={32} thickness={4} />
    <Typography
      variant="body2"
      sx={{
        color: "#666666",
        fontSize: "14px",
        fontWeight: 500,
      }}
    >
      Loading messages...
    </Typography>
  </ModernLoadingIndicatorWrapper>
);

const ModernLoadingIndicatorWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  gap: "12px",
  "& .MuiCircularProgress-root": {
    color: theme?.mainColor || "#E87975",
  },
}));

const SkeletonMessage = ({ isMyMessage }) => (
  <Box
    sx={{
      width: "100%",
      display: "flex",
      justifyContent: isMyMessage ? "flex-end" : "flex-start",
      margin: "8px 16px",
    }}
  >
    <Box
      sx={{
        minWidth: "50px",
        maxWidth: "70%",
        padding: "12px 16px",
        borderRadius: isMyMessage ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
        backgroundColor: isMyMessage ? "#F0F0F0" : "#F5F5F5",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Skeleton
        variant="text"
        width={isMyMessage ? "60%" : "80%"}
        height={16}
        sx={{
          backgroundColor: isMyMessage ? "#E0E0E0" : "#EEEEEE",
          borderRadius: "4px",
        }}
      />
      <Skeleton
        variant="text"
        width={isMyMessage ? "40%" : "50%"}
        height={12}
        sx={{
          backgroundColor: isMyMessage ? "#E0E0E0" : "#EEEEEE",
          borderRadius: "4px",
        }}
      />
    </Box>
  </Box>
);

const IsBlockedText = styled(Typography)({
  color: (props) => props.theme?.mainColor || "#E87975",
});

const Row = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  padding: "20px",
});
